import { useCallback, useMemo, useState } from "react";

import { useAppProperties } from "@/hooks";
import { useTranslate, useUser } from "@/providers/context";
import { ColumnModal, Input } from "@/components";
import { logout } from "@/api";
import {
  APPLE_STORE,
  GOOGLE_PLAY,
  PRODUCT_PLATFORM_APPLE,
  PRODUCT_PLATFORM_STRIPE,
} from "@/const.ts";

import { ProfileHeader } from "./components/ProfileHeader.tsx";
import { Section } from "./components/Section.tsx";
import { ProfileRow } from "./components/ProfileRow.tsx";
import { SignInOutButton } from "./components/SignInOutButton.tsx";
import { useRequestDeletion } from "./hooks/useRequestDeletion.tsx";
import { getUrl } from "./utils/util.ts";
import { Header } from "@/app/modules/Navigation";

export const DataPrivacyScreen = () => {
  const { data: properties } = useAppProperties();
  const { translate } = useTranslate();
  const { email, uid, platform } = useUser();
  const [open, setOpen] = useState(false);
  const [proceed, setProceed] = useState(false);
  const [emailConfirm, setEmailConfirm] = useState("");
  const requestDeletion = useRequestDeletion();
  const isSignedIn = !!uid;

  const confirmDisabled = useMemo(() => {
    if (proceed) {
      return emailConfirm !== email;
    }
    return false;
  }, [emailConfirm, email, proceed]);

  const reset = useCallback(() => {
    setEmailConfirm("");
    setProceed(false);
    setOpen(false);
  }, []);

  const modalContent = useMemo(() => {
    if (PRODUCT_PLATFORM_STRIPE !== platform) {
      const store =
        platform === PRODUCT_PLATFORM_APPLE ? APPLE_STORE : GOOGLE_PLAY;
      return (
        <>
          {translate(
            "This request is to remove your personal data from our system. Your subscription was purchased from the",
          )}{" "}
          {store}.{" "}
          <b>
            {translate("You will need to cancel it from the")} {store}{" "}
            {translate("separately")}
          </b>
          <br />
          <br />
          {translate(
            "To confirm data deletion request, type your email, then click Confirm Request. All personal data may take up to 30 days to be fully deleted.",
          )}
        </>
      );
    }
    return (
      <>
        {translate(
          "You will no longer be able to sign in to the app using this email and lose the remaining period of your subscription.",
        )}
        <br />
        <br />
        {translate(
          "To confirm data deletion request and cancel subscription, type your email, then click Confirm Request. All personal data may take up to 30days to be fully deleted.",
        )}
      </>
    );
  }, [platform, translate]);

  return (
    <div className="px-7">
      <Header />
      <div className="flex justify-center">
        <div className="flex w-[47%] max-w-[622px] flex-col items-center">
          <ProfileHeader />
          <Section heading={translate("Data & Privacy")} hideSeparator>
            <ProfileRow
              name={translate("Privacy Policy")}
              disabled={!uid}
              onClick={() =>
                window.open(
                  getUrl(properties?.PrivacyPolicy) ||
                    "https://vidapp.com/privacy-policy",
                )
              }
            />
            <ColumnModal
              title={
                proceed && platform === PRODUCT_PLATFORM_STRIPE
                  ? translate("Important! You will lose access.")
                  : translate("Request Data Deletion")
              }
              open={open}
              onCancel={reset}
              cols={3}
              danger
              confirmIsLoading={requestDeletion.isLoading}
              confirmDisabled={confirmDisabled || requestDeletion.isLoading}
              onConfirm={async () => {
                if (!proceed) {
                  setProceed(true);
                  return;
                }
                requestDeletion.mutate(undefined, {
                  onSuccess: async () => {
                    await logout({ user_id: uid! });
                  },
                });
              }}
              primaryText={
                platform === PRODUCT_PLATFORM_STRIPE && !proceed
                  ? translate("Proceed")
                  : translate("Confirm request")
              }
              triggerButton={
                <>
                  <ProfileRow
                    name={translate("Request personal data deletion")}
                    disabled={!isSignedIn}
                    onClick={() => setOpen(true)}
                  />
                </>
              }
            >
              <div className="pt-1">
                {!proceed && platform === PRODUCT_PLATFORM_STRIPE ? (
                  <div className="p1-regular px-[24px] pb-[24px]">
                    {translate(
                      "This request will cancel your subscription and remove your personal data from our system",
                    )}
                    .
                    <br />
                    <br />
                    {translate("Are you sure you want to proceed?")}
                    <br />
                  </div>
                ) : (
                  <div className="p1-regular px-[24px] pb-[24px]">
                    {modalContent}
                    <Input
                      className="mt-6"
                      value={emailConfirm}
                      placeholder={email}
                      onChange={(e) => setEmailConfirm(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </ColumnModal>
          </Section>
          <SignInOutButton />
        </div>
      </div>
    </div>
  );
};
